import { useScrollTop } from 'Core/hooks/useScrollTop';
import React from 'react';
import FinancingStatusMessage from 'Modules/fortiva/components/FinancingStatusMessage';

import { useGetErrorConfig } from 'Modules/fortiva/hooks/useGetErrorConfig';
import CreateApplication from 'Modules/fortiva/routes/CreateApplication';
import FinancingRequalify from 'Modules/fortiva/routes/FinancingRequalify';
import FinancingDeclined from 'Modules/fortiva/routes/FinancingDeclined';
import FinancingVerificationRequired from 'Modules/fortiva/routes/FinancingVerificationRequired';
import FinancingAlreadyProcessed from 'Modules/fortiva/routes/FinancingAlreadyProcessed';
import FinancingDecisionPending from 'Modules/fortiva/routes/FinancingDecisionPending';

const isFrozenCreditCase = (application, nextAction) => {
  const correctionRequired = application?.payload?.correction_required;
  return (
    nextAction === 'CORRECTION_REQUIRED' && correctionRequired?.message?.includes('{FROZEN_CREDIT}')
  );
};

const isReviewDeclinedCase = (application, nextAction) => {
  const correctionRequired = application?.payload?.correction_required;
  return (
    nextAction === 'DECISION_PENDING' &&
    correctionRequired?.status === 'REVIEW' &&
    correctionRequired?.message ===
      'Invalid request for application-requalify: Application status must be incomplete'
  );
};

const isApplicationPendingCase = (application, nextAction) => {
  const correctionRequired = application?.payload?.correction_required;
  return nextAction === 'DECISION_PENDING' && correctionRequired?.status === 'APPLICATION_PENDING';
};

const isExistingCustomerCase = (application, nextAction) => {
  const correctionRequired = application?.payload?.correction_required;
  return (
    nextAction === 'DECISION_PENDING' &&
    correctionRequired?.message?.includes('{EXISTING_CUSTOMER}')
  );
};

const isIdVerificationFailedCase = (application, nextAction) => {
  const correctionRequired = application?.payload?.correction_required;
  return (
    nextAction === 'DECLINED' &&
    correctionRequired?.status === 'REJECTED' &&
    correctionRequired?.message === 'truvalidateIDVFailed'
  );
};

export default function FinancingExceptions({
  customer_id,
  application,
  nextAction,
  onCustomerIdentityNumber,
}) {
  document.title = 'Payment Portal - Financing';
  useScrollTop();

  const purpose = nextAction === 'DECISION_PENDING' ? 'create' : 'update';
  const errorConfig = useGetErrorConfig(application);

  // Check for frozen credit case - redirect to requalify flow
  if (isFrozenCreditCase(application, nextAction)) {
    return <FinancingRequalify />;
  }

  // Existing customer match while decision is pending - show the already-processed page
  if (isExistingCustomerCase(application, nextAction)) {
    return <FinancingAlreadyProcessed />;
  }

  // Application is still awaiting a decision from Fortiva - show "check back later" page
  if (isApplicationPendingCase(application, nextAction)) {
    return <FinancingDecisionPending />;
  }

  // Check for review/declined case - show declined page without reapply option
  if (isReviewDeclinedCase(application, nextAction)) {
    return <FinancingDeclined allowCreateApplication={false} application={application} />;
  }

  // Check for ID verification failed case - show verification required flow
  if (isIdVerificationFailedCase(application, nextAction)) {
    return <FinancingVerificationRequired />;
  }

  // For DECLINED cases that don't match specific exceptions, show declined page
  if (nextAction === 'DECLINED') {
    return <FinancingDeclined application={application} />;
  }

  if (errorConfig?.message) {
    return (
      <CreateApplication
        type="primary"
        customer_id={customer_id}
        purpose={purpose}
        errorConfig={errorConfig}
        onCustomerIdentityNumber={onCustomerIdentityNumber}
      />
    );
  }

  return <FinancingStatusMessage nextAction={nextAction} message={errorConfig.message} />;
}
